import React, { useContext, useState } from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import ArtStage from '../../ArtStage';
import ActionBar from '../../ActionBar';
import Details from '../../DeviationViews/Details';
import TextualContent from '../../DeviationViews/TextualContent';
import StageBackground from '../../DeviationBackground/StageBackground';
import { useDeviationViewContext } from '../../Context/DeviationViewContext';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import MonetizationBanner from '../../banners/MonetizationBanner';
import UserGiftSaleBanner from '../_partials/UserGiftSaleBanner';
import DesktopLayout from '../_partials/DesktopLayout';

import s from './DesktopTextualPage.scss';

export interface Props {
  deviation: PapiDeviation;
}

const DesktopTextualPage: React.FC<Props> = ({ deviation }) => {
  const { isTheater } = useDeviationViewContext();
  const isLegacyJournal =
    getDeviationType(deviation) === DeviationTypes.JOURNAL &&
    deviation.textContent?.html.type === 'writer';
  const isLegacyView =
    isLegacyJournal && !deviation.extended?.journalSkinFullwidth;
  const isFullWidthView =
    isLegacyJournal && deviation.extended?.journalSkinFullwidth;
  const contentClassName = classnames(
    s['content'],
    isLegacyView && s['is-legacy-journal'],
    isFullWidthView && s['is-fullwidth-skin']
  );
  const { width: screenSize } = useContext(ScreenSizeContext);

  // we'll force a re-render of the page when a countdown in the offer banner of the monetization strip
  // reaches 0 (onAdoptableSaleEnd). This will update the actionbar to change from the 'make an offer'
  // button to the 'view offers' button to show the sale is over
  const [hasAdoptionEnded, setHasAdoptionEnded] = useState(false);

  return (
    <DesktopLayout
      className={classnames(isTheater && s['theater'])}
      deviation={deviation}
      stageContent={
        <>
          <ArtStage deviation={deviation} />
          <div className={s['strip-tier-promotion-wrapper']}>
            <MonetizationBanner
              className={s['strip-tier-promotion']}
              deviation={deviation}
              onAdoptableSaleEnd={() => setHasAdoptionEnded(true)}
            />
          </div>
          <div className={contentClassName}>
            <ActionBar
              // maintain a key so we can force a re-render
              key={`actionbar-${hasAdoptionEnded}`}
              deviation={deviation}
            />
          </div>
          <div className={contentClassName}>
            <TextualContent deviation={deviation} />
          </div>
        </>
      }
      metaContent={
        <>
          <Details className={contentClassName} deviation={deviation} />
          <UserGiftSaleBanner user={deviation.author} />
        </>
      }
      backgroundContent={
        <StageBackground
          deviation={deviation}
          width={Math.max(screenSize, 780)}
        />
      }
      withSecondaryActionBar
    />
  );
};
DesktopTextualPage.displayName = 'DesktopTextualPage';

export default DesktopTextualPage;
